import {
  Activity,
  AlertTriangle,
  Award,
  BarChart,
  Bot,
  Brain,
  Briefcase,
  Clock,
  CreditCard,
  FlaskConical,
  Gauge,
  Globe,
  Inbox,
  Landmark,
  Mail,
  Map as MapIcon,
  Plane,
  Receipt,
  Scale,
  Settings,
  Settings2,
  Shield,
  ShieldAlert,
  Tag,
  TrendingUp,
  Users,
  ConciergeBell,
  BadgeCheck,
} from 'lucide-react';
import { createPageUrl } from '@/utils';
import { ADMIN_LANDING_URL } from './navigation';

/**
 * The admin pages, grouped the way a large console groups them: a handful of
 * areas, each holding its pages, so the mobile menu reads as six categories
 * instead of twenty-odd rows. Every admin page with a route belongs to exactly
 * one area (the test pins that), including the ones that had no menu entry at
 * all on mobile (Cash Ladder, Status rewards, Destination Library, the route
 * map, the Brain).
 *
 * Who sees what (visibleAdminCategories): admins see everything; the
 * `concierge` desk role sees only Inquiries, the queue it works; investors see
 * only Analytics; Settings is the founder's.
 */
export const ADMIN_NAV_CATEGORIES = Object.freeze([
  {
    id: 'concierge',
    title: 'Concierge',
    icon: ConciergeBell,
    items: [
      { title: 'Inquiries', url: ADMIN_LANDING_URL, icon: Inbox, conciergeOk: true },
      { title: 'Staff', url: createPageUrl('Staff'), icon: Bot },
    ],
  },
  {
    id: 'customers',
    title: 'Customers',
    icon: Users,
    items: [
      { title: 'Users', url: createPageUrl('Users'), icon: Users },
      { title: 'Activity', url: '/activity', icon: Activity },
      { title: 'Live Flights', url: '/live-flights', icon: Plane },
      { title: 'Carts', url: createPageUrl('Carts'), icon: CreditCard },
      { title: 'Black List', url: createPageUrl('BlackList'), icon: ShieldAlert },
    ],
  },
  {
    id: 'money',
    title: 'Money',
    icon: Landmark,
    items: [
      { title: 'Accounting', url: createPageUrl('Accounting'), icon: Receipt },
      { title: 'Cash Ladder', url: '/cash-ladder', icon: Scale },
      { title: 'Settlement Desk', url: '/settlement-desk', icon: Landmark },
      { title: 'Past Due', url: createPageUrl('Past Due'), icon: Clock },
      { title: 'Collections', url: createPageUrl('Collections'), icon: Briefcase },
      { title: 'Status rewards', url: '/AccountingRewards', icon: Award },
    ],
  },
  {
    id: 'risk',
    title: 'Risk',
    icon: Shield,
    items: [
      { title: 'Risk Review', url: createPageUrl('RiskReview'), icon: ShieldAlert },
      { title: 'Risk Insights', url: createPageUrl('RiskInsights'), icon: Shield },
      { title: 'Portfolio Intelligence', url: createPageUrl('PortfolioIntelligence'), icon: Gauge },
      { title: 'Model Performance', url: '/model-performance', icon: FlaskConical },
    ],
  },
  {
    id: 'growth',
    title: 'Growth',
    icon: TrendingUp,
    items: [
      { title: 'Analytics', url: createPageUrl('Analytics'), icon: BarChart, investorOk: true },
      { title: 'Marketing', url: createPageUrl('Marketing'), icon: Mail },
      { title: 'Discount Codes', url: '/discount-codes', icon: Tag },
      { title: 'Earn Credit Review', url: '/earn-review', icon: BadgeCheck },
      { title: 'Destination Library', url: '/destination-content', icon: Globe },
      { title: 'Route Map', url: '/map', icon: MapIcon },
    ],
  },
  {
    id: 'system',
    title: 'System',
    icon: Settings2,
    items: [
      { title: 'Error Log', url: createPageUrl('ErrorLog'), icon: AlertTriangle },
      { title: 'Brain', url: '/brain', icon: Brain },
      { title: 'Settings', url: createPageUrl('Settings'), icon: Settings, founderOnly: true },
    ],
  },
]);

/** The categories this person may open, each holding only the pages they may
 *  see; a category left empty is dropped. */
export function visibleAdminCategories({ isAdmin = false, isInvestor = false, isConcierge = false, isFounder = false } = {}) {
  const canSee = (item) => {
    if (item.founderOnly) return isFounder;
    if (isAdmin) return true;
    return Boolean((item.investorOk && isInvestor) || (item.conciergeOk && isConcierge));
  };
  return ADMIN_NAV_CATEGORIES
    .map((category) => ({ ...category, items: category.items.filter(canSee) }))
    .filter((category) => category.items.length > 0);
}

/** Routes match case-insensitively ("/Accounting" and "/accounting" are one page). */
export const isSamePath = (a, b) => Boolean(a && b) && String(a).toLowerCase() === String(b).toLowerCase();

/** The category holding the page at `pathname`, or null. */
export function categoryIdForPath(categories, pathname) {
  const hit = (categories || []).find((c) => c.items.some((item) => isSamePath(item.url, pathname)));
  return hit ? hit.id : null;
}

/** Pages matching a search, across every category, each with the category it
 *  sits in. A category's own name matches all of its pages. */
export function searchAdminPages(categories, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return [];
  return (categories || []).flatMap((c) => c.items
    .filter((item) => item.title.toLowerCase().includes(needle) || c.title.toLowerCase().includes(needle))
    .map((item) => ({ item, category: c.title })));
}
